import { formatISO, parseISO, setDay } from 'date-fns';
import { forwardRef, useMemo } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import { DatePicker } from '../../../components/ui/date-picker.tsx';
import { InputNumber } from '../../../components/ui/input-number.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { formatWeekdayShort } from '../../../lib/date-helpers.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { cn } from '../../../lib/utils.ts';
import {
    DEFAULT_INTERVAL,
    generateRecurrenceString,
    getDefaultRecurrence,
    parseRecurrenceString,
} from '../../../utils/rrule.ts';

type RRuleInputProps = {
    startDate: string;
    value?: string;
    onChange: (value: string) => void;
    disabled?: boolean;
};

const MAX_INTERVAL = 99;
const WEEKDAYS_OPTIONS = Array.from({ length: 7 }, (_, i) => (i < 6 ? i + 1 : 0));

export const RRuleInput = forwardRef<HTMLDivElement, RRuleInputProps>(function RRuleInput(
    { value, onChange, startDate, disabled = false },
    ref,
) {
    const { t, locale } = useLocale('events');

    const {
        weekdays: selectedDays,
        interval,
        excludedDates,
        until,
    } = useMemo(() => {
        const defaultWeekday = new Date(startDate).getDay();
        const options = parseRecurrenceString(value ?? getDefaultRecurrence(), startDate);

        if (!options) {
            return {
                weekdays: [defaultWeekday],
                interval: DEFAULT_INTERVAL,
                excludedDates: [],
                until: undefined as string | undefined,
            };
        }

        return {
            weekdays: [defaultWeekday],
            ...options,
        };
    }, [value, startDate]);

    const handleIntervalChange = (newValue: number) => {
        onChange(
            generateRecurrenceString(
                {
                    interval: newValue,
                    weekdays: selectedDays,
                    excludedDates,
                    until,
                },
                startDate,
            ),
        );
    };

    const handleWeekdayToggle = (day: number) => {
        const selectedValues = new Set(selectedDays);

        if (selectedValues.has(day)) {
            selectedValues.delete(day);
        } else {
            selectedValues.add(day);
        }

        onChange(
            generateRecurrenceString(
                {
                    weekdays: [...selectedValues],
                    interval,
                    excludedDates,
                    until,
                },
                startDate,
            ),
        );
    };

    const handleUntilChange = (nextUntil: string | undefined) => {
        onChange(
            generateRecurrenceString(
                {
                    interval,
                    weekdays: selectedDays,
                    excludedDates,
                    until: nextUntil,
                },
                startDate,
            ),
        );
    };

    return (
        <div ref={ref} className="flex flex-col gap-3">
            <div className="flex gap-3 flex-col sm:flex-row">
                <div className="flex flex-col gap-1.5">
                    <div>
                        <Text color="neutral-950">{t('events__form_recurrence_repeat_every')}</Text>
                    </div>
                    <div className="flex items-center gap-3">
                        <InputNumber
                            aria-label={t('events__form_recurrence_repeat_every')}
                            data-testid="recurrence-interval"
                            className="w-20 text-left hover:border-neutral-400 focus-visible:border-blue-600 focus-visible:ring-1 focus-visible:ring-blue-200 focus-visible:ring-offset-0"
                            value={interval}
                            decimalScale={0}
                            allowNegative={false}
                            isAllowed={({ floatValue }) =>
                                floatValue === undefined || (floatValue >= 1 && floatValue <= MAX_INTERVAL)
                            }
                            onValueChange={(values) => {
                                if (values.floatValue !== undefined) {
                                    handleIntervalChange(values.floatValue);
                                }
                            }}
                            disabled={disabled}
                        />
                        <Text color="neutral-950">{t('events__form_recurrence_weeks')}</Text>
                    </div>
                </div>
                <div className="flex flex-col gap-1.5">
                    <div>
                        <Text color="neutral-950">{t('events__form_recurrence_on')}</Text>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {WEEKDAYS_OPTIONS.map((weekDay) => {
                            const isSelected = selectedDays.includes(weekDay);

                            return (
                                <Button
                                    key={weekDay}
                                    variant="outline"
                                    size="icon-s"
                                    className={cn('text-sm', {
                                        'bg-blue-50': isSelected,
                                    })}
                                    rounded={true}
                                    selected={isSelected}
                                    onClick={() => {
                                        handleWeekdayToggle(weekDay);
                                    }}
                                    disabled={disabled}
                                >
                                    {formatWeekdayShort(setDay(new Date(), weekDay), locale).charAt(0)}
                                </Button>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className="flex flex-col gap-1.5">
                <div>
                    <Text color="neutral-950">{t('events__form_recurrence_until_label')}</Text>
                </div>
                <div className="flex items-center">
                    <DatePicker
                        mode="single"
                        value={until ? parseISO(until) : undefined}
                        onChange={(date) => {
                            handleUntilChange(date ? formatISO(date, { representation: 'date' }) : undefined);
                        }}
                        disabled={disabled}
                        clearable
                    />
                </div>
            </div>
        </div>
    );
});
